import { useEffect, useState } from 'react';
import { useModel } from 'umi';
import { fetchBranchesRequest } from '@/services/api/location';
import { processAPIResponseNow } from '@/utils/request';

export type OwnBranchService = {
  id: string;
  branchId?: string;
  branchName?: string;
  serviceName?: string;
};

// Services of `GET /branches?full=1&agent=` the user is an agent of. Filtered again
// here: the legacy backend still returns every in-scope branch for that query.
export const getOwnBranchServices = (branches: any[], userId: string): OwnBranchService[] =>
  (branches || []).flatMap((branch) =>
    (branch?.services || [])
      .filter(
        (service: any) =>
          !service.deleted_at &&
          (service.agents || []).some((agent: any) => `${agent?.id}` === `${userId}`),
      )
      .map((service: any) => ({
        id: `${service.id}`,
        branchId: branch.id,
        branchName: branch.name,
        serviceName: service.service_type?.name,
      })),
  );

// Only a user with a single service has an unambiguous one to preselect.
export const getFixedBranchService = (services: OwnBranchService[]): OwnBranchService | null =>
  services.length === 1 ? services[0] : null;

export const useFixedBranchService = (enabled: boolean) => {
  const { initialState } = useModel('@@initialState');
  const userId = initialState?.currentUser?.User?.alias;
  const [service, setService] = useState<OwnBranchService | null>(null);
  const [loading, setLoading] = useState(enabled && !!userId);

  useEffect(() => {
    if (!enabled || !userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchBranchesRequest({ query: { in_scope: 1, full: 1, agent: userId } })
      .then((response) => {
        if (cancelled) return;
        processAPIResponseNow(
          response,
          {
            onSuccess(branches: any[]) {
              setService(getFixedBranchService(getOwnBranchServices(branches, userId)));
            },
          },
          'data',
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [enabled, userId]);

  return { service: enabled ? service : null, loading: enabled && loading };
};
